'use client';

import React, { useEffect, useState } from 'react';
import { Box, VStack } from '@skin-seoul/design-system';
import ProductBasicInfo from './components/ProductBasicInfo';
import ProductOptionsSelection from './components/ProductOptionsSelection';
import ProductQuantitySelector from './components/ProductQuantitySelector';
import ProductActionButtons from './components/ProductActionButtons';
import ProductBenefits from './components/ProductBenefits';
import { RECENTLY_VIEWED_PRODUCTS_KEY, useRegionCurrency, ProductType, useCartService, useProductService, createAddToCartParams, createBuyNowParams } from '@skin-seoul/service';
import Cookies from 'js-cookie';
import { useSafeRouter } from '@/hooks';
import { useCartDrawer } from '@skin-seoul/shared-components';
import { useParams } from 'next/navigation';
import BMSMCampaignBannerSection from './BMSMCampaignBannerSection';
import { type LogParameters, usePageLogger } from '@skin-seoul/utils';
import { getDeviceId } from '@/utils/deviceId';
interface ProductBuySectionProps {
  initialSelectedOption: string;
}
const ProductBuySection = ({
  initialSelectedOption
}: ProductBuySectionProps) => {
  const {
    useGetProductsDetail
  } = useProductService();
  const logger = usePageLogger();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const {
    data: {
      product
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const {
    mutate: addToCart,
    status
  } = useCartService().useAddToCart({
    currency
  });
  const {
    onCartOpen
  } = useCartDrawer();
  const router = useSafeRouter();
  const initialSelectedOptionProduct = (() => {
    if (product.type === ProductType.Simple) {
      return product;
    }
    return product.variations.find(variation => variation.productId === initialSelectedOption);
  })();
  const [selectedOption, setSelectedOption] = useState<string | undefined>(initialSelectedOptionProduct?.status !== 'outofstock' ? initialSelectedOptionProduct?.productId : undefined);
  const [quantity, setQuantity] = useState(1);
  const selectedOptionProduct = (() => {
    if (product.type === ProductType.Simple) {
      return product;
    }
    return product.variations.find(variation => variation.productId === selectedOption);
  })();
  const handleQuantityChange = (increment: boolean) => {
    if (increment) {
      setQuantity(prev => Math.min(prev + 1, 999));
    } else {
      setQuantity(prev => Math.max(prev - 1, 1));
    }
  };
  const handleAddToBag = () => {
    if (!selectedOptionProduct) {
      console.error('selectedOptionProduct is not found');
      return;
    }
    const addToCartGAParams = createAddToCartParams({
      product,
      selectedOptionProduct,
      quantity,
      currency,
      deviceId: getDeviceId()
    });
    logger?.click('add_to_cart', addToCartGAParams as LogParameters);
    addToCart({
      sku: selectedOptionProduct.sku,
      quantity,
      currency
    });
    onCartOpen();
  };
  const handleBuyNow = () => {
    if (!selectedOptionProduct) {
      console.error('selectedOptionProduct is not found');
      return;
    }
    const buyNowParams = createBuyNowParams({
      selectedOptionProduct,
      quantity
    });
    logger?.click('click_buy_now', buyNowParams);
    router.push(`/checkout/?add-to-cart=${selectedOption}&quantity=${quantity}`);
  };
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: `${product.brand} - ${product.name}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (error) {
      console.error('share error', error);
    }
  };
  const handleWishlistToggle = (action: 'add' | 'remove') => {
    if (!selectedOptionProduct) {
      return;
    }
    const itemPrice = selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice;
    const itemVariant = `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`;
    logger?.click('wishlist_click', {
      action,
      x_fb_cd_content_type: 'product',
      x_fb_cd_content_name: product.name,
      x_fb_cd_content_ids: product.productId,
      value: itemPrice.toString(),
      currency,
      items: [{
        item_id: product.productId,
        item_name: product.name,
        item_brand: product.brand || '',
        item_sku: product.sku,
        price: itemPrice.toString(),
        item_category: product.category?.[0] || '',
        item_category2: product.category?.[1] || '',
        item_variant: itemVariant,
        product_image_url: product.productImageUrls?.[0] || ''
      }],
      item_list_name: 'PDP - Wishlist',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      page_type: 'pdp',
      event_time: Date.now()
    });
  };
  useEffect(() => {
    if (!product) {
      return;
    }
    const recentlyViewedProducts = Cookies.get(RECENTLY_VIEWED_PRODUCTS_KEY) || '';
    const recentlyViewedProductsArray = recentlyViewedProducts.split('|').filter(Boolean);
    const productIdStr = product.productId.toString();
    const filteredArray = recentlyViewedProductsArray.filter(id => id !== productIdStr);
    filteredArray.unshift(productIdStr);
    Cookies.set(RECENTLY_VIEWED_PRODUCTS_KEY, filteredArray.join('|'));
  }, [product]);
  return <VStack width="100%" gap={0} data-sentry-element="VStack" data-sentry-component="ProductBuySection" data-sentry-source-file="ProductBuySection.tsx">
      <ProductBasicInfo onShare={handleShare} selectedOption={selectedOption ?? initialSelectedOption} data-sentry-element="ProductBasicInfo" data-sentry-source-file="ProductBuySection.tsx" />
      {product.status !== 'outofstock' && <BMSMCampaignBannerSection />}
      {product.type === ProductType.Variable && <ProductOptionsSelection product={product} selectedOption={selectedOption} onOptionChange={setSelectedOption} />}

      <Box width="100%" background="white" paddingY={{
      base: 3,
      tablet: 6
    }} paddingX={{
      base: 4,
      tablet: 4
    }} data-sentry-element="Box" data-sentry-source-file="ProductBuySection.tsx">
        <VStack gap={4} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBuySection.tsx">
          <ProductQuantitySelector quantity={quantity} onQuantityChange={handleQuantityChange} data-sentry-element="ProductQuantitySelector" data-sentry-source-file="ProductBuySection.tsx" />
          <ProductActionButtons onAddToBag={handleAddToBag} onBuyNow={handleBuyNow} sku={product?.sku} productStatus={product.status} addToBagStatus={status} hasSelectedOption={Boolean(selectedOptionProduct)} onWishlistToggle={handleWishlistToggle} data-sentry-element="ProductActionButtons" data-sentry-source-file="ProductBuySection.tsx" />
        </VStack>
      </Box>

      <ProductBenefits data-sentry-element="ProductBenefits" data-sentry-source-file="ProductBuySection.tsx" />
    </VStack>;
};
export default ProductBuySection;